import { useCallback, useEffect, useRef, useState } from 'react';
import { runServiceTestVerify } from '../verify/ServiceTestVerifyHelper';

/**
 * Hook that manages the verify lifecycle for service tests.
 * Encapsulates verify state, AbortController, and cleanup effects.
 * Extracted from TestDetailPanel.jsx for separation of concerns
 * (Phase 8 — SPLIT-01, READ-02).
 *
 * @param {object} params
 * @param {object} params.testType - Current test type object
 * @param {React.RefObject} params.matchingAgentsRef - Ref to matching agents array
 * @param {string} params.userName - Current user's name
 * @param {string} params.userId - Current user's ID
 * @param {object} params.dataService - API data service instance
 * @param {string} params.recordId - Current test record ID
 * @param {string} params.verifyResultSectionRef - verify Result Section Ref
 * @returns {{ showVerify: boolean, verifyLoading: boolean, verifyResult: object|null, onVerify: Function }}
 */
export function useVerifyLifecycle({
    testType,
    matchingAgentsRef,
    userName,
    userId,
    dataService,
    recordId,
    verifyResultSectionRef,
}) {
    const [showVerify, setShowVerify] = useState(false);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);
    const verifyAbortRef = useRef(null);

    const onVerify = useCallback(
        (validatedFormData) => {
            // Abort any in-flight verify before starting a new one
            if (verifyAbortRef.current) {
                verifyAbortRef.current.abort();
            }
            const controller = new AbortController();
            verifyAbortRef.current = controller;

            runServiceTestVerify({
                formData: validatedFormData,
                testType,
                agents: matchingAgentsRef.current || [],
                userName,
                userId,
                dataService,
                setShowVerify,
                setVerifyLoading,
                setVerifyResult,
                testId: recordId,
                signal: controller.signal,
                verifyResultSectionRef,
            });
        },
        [testType, matchingAgentsRef, userName, userId, dataService, recordId, verifyResultSectionRef]
    );

    // Reset verify state and cancel in-flight polling when switching tests
    useEffect(() => {
        if (verifyAbortRef.current) {
            verifyAbortRef.current.abort();
            verifyAbortRef.current = null;
        }
        setShowVerify(false);
        setVerifyResult(null);
    }, [recordId]);

    // Cancel verify polling on component unmount
    useEffect(() => {
        return () => {
            if (verifyAbortRef.current) {
                verifyAbortRef.current.abort();
            }
        };
    }, []);

    return { showVerify, verifyLoading, verifyResult, onVerify };
}
